/**
 * 管理者の閲覧範囲の画面（課題 #100）で使う判定をまとめる。
 *
 * 権限は「壊れたら閉じる」側に倒してある（BE）。拠点管理者に部署を 1 つも
 * 割り当てないと、その管理者は何も見られない。消し忘れが「何も見えない」として
 * 現れるので、画面では保存の前と一覧の両方で気づけるようにする。
 */
import type { AdminScopeUpdateRequest, DepartmentResponse } from '../api/client';

export type AdminScopeKind = 'all' | 'departments';

/** 画面上の区分。BE が知らない値を返しても、見える範囲の狭い拠点管理者側に倒す */
export function scopeKindOf(adminScope: string | null | undefined): AdminScopeKind {
  return adminScope === 'all' ? 'all' : 'departments';
}

/** 自分自身の行は変更できない（BE も 403）。全社管理者が 0 人になると誰も戻せなくなるため */
export function canEditScope(targetUserId: string, currentUserId: string | null | undefined): boolean {
  return Boolean(currentUserId) && targetUserId !== currentUserId;
}

/**
 * チェックリストに出す部署。有効な部署に加え、無効化済みでも今割り当てている部署は残す。
 * 消してしまうと、保存したときに黙って割り当てから外れる。
 */
export function departmentsForChecklist(
  departments: DepartmentResponse[],
  selectedIds: string[]
): DepartmentResponse[] {
  const selected = new Set(selectedIds);
  return departments
    .filter((d) => d.status !== 'inactive' || selected.has(d.id))
    .sort((a, b) => a.name.localeCompare(b.name, 'ja'));
}

/** 保存する内容。全社管理者なら部署は送らない（空）。重複は除く */
export function buildScopeUpdate(kind: AdminScopeKind, departmentIds: string[]): AdminScopeUpdateRequest {
  return {
    admin_scope: kind,
    department_ids: kind === 'departments' ? Array.from(new Set(departmentIds)) : [],
  };
}

/** 拠点管理者なのに部署が 1 つも無い（保存すると何も見えなくなる） */
export function wouldSeeNothing(kind: AdminScopeKind, departmentIds: string[]): boolean {
  return kind === 'departments' && departmentIds.length === 0;
}

/**
 * 所属人数が匿名化の閾値を下回る部署か。人数や閾値が分からないときは判定しない。
 * 下回る部署だけを割り当てると、その拠点管理者には集計が表示されない（#100 の東京の例）。
 */
export function isBelowAnonymityThreshold(
  userCount: number | null | undefined,
  kAnonymity: number | null | undefined
): boolean {
  return userCount != null && kAnonymity != null && userCount < kAnonymity;
}
